import { Alert, Button, Card, Col, Collapse, Divider, Row, Space, Tag, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useInitializeUserVerificationHistory } from "../../../../function/accountVerification";
import { verificationRefetchState } from "../../../../state";
import { extractLocalizedValue, getStatusColor } from "../../../../common";
import { VerificationStep } from "../../../../common/constant";
import { LockOutlined } from "@ant-design/icons";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import {
    apiCancelVerificationHistory,
    apiDuplicateUserVerificationHistory
} from "../../../../../../api/restful/verification";

function SelectTier(props) {
    const {
        userVerificationHistories,
        goTo,
        setSelectedTier,
        userVerificationTiers,
        accessRightsListCategory,
        blockedByFailedTier,
        failedTierHistory,
        blockedTierHistory,
    } = props

    const { t } = useTranslation();
    const { initializeUserVerificationHistory } = useInitializeUserVerificationHistory();
    const { refetchUserVerificationProfile, refetchUserVerificationHistories } = useRecoilValue(verificationRefetchState);
    const [isCancelling, setIsCancelling] = useState(false);

    const handleCancelBlockedTier = async () => {
        if (!blockedTierHistory?._id) return;
        try {
            setIsCancelling(true);
            await apiCancelVerificationHistory(blockedTierHistory._id);
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ]);
        } catch (e) {
            console.error("handleCancelBlockedTier error:", e?.message);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSelectTier = async (tier, startNewRecord = false) => {
        setSelectedTier(tier);
        const existingHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
        const recordExist = ["canceled", "verification_failed"].includes(existingHistory?.status)

        if (!existingHistory?._id || (recordExist && startNewRecord)) {
            try {
                const result = await initializeUserVerificationHistory(tier._id);

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    if (startNewRecord) return

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }

            } catch (error) {
                console.error("error initializing verification history:", error);
            }
        } else if (recordExist) {
            try {
                const result = await apiDuplicateUserVerificationHistory({ _id: existingHistory?._id })

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }
            } catch (e) {
                console.error("error duplicating verification history:", e);
            }
        } else {
            goTo(VerificationStep.Document);
        }

    }

    const getTierStatus = (tierId) => {
        const history = userVerificationHistories?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.find((h) => (h.tierSetting?._id || h.tierSetting) === tierId);
        return history?.status ?? null;
    }

    const getNextTier = (history, tiers) => {
        const sortedHistory = [...(history || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        const validHistory = sortedHistory.find(h => h.status === "pending_verification" || h.status === "verified")
        if (!validHistory) return tiers.find(t => t.tierLevel === 1)

        let nextTierLevel = validHistory.tierLevel + 1

        while (true) {
            const nextTier = tiers.find(t => t.tierLevel === nextTierLevel)
            if (!nextTier) return null

            const nextTierHistory = sortedHistory.find(h => h.tierLevel === nextTierLevel)

            if (
                !nextTierHistory ||
                (nextTierHistory.status !== "pending_verification" && nextTierHistory.status !== "verified")
            ) {
                return nextTier
            }

            nextTierLevel++
        }
    }

    const currentTierAvailable = getNextTier(userVerificationHistories, userVerificationTiers)
    const currentTierAvailableId = currentTierAvailable?._id
    const currentTierLevel = currentTierAvailable?.tierLevel || 0

    const standaloneTier = userVerificationTiers?.find(d => d?.type === "standalone")

    const mapCategoryAccess = (category, access) => {
        const result = {}

        for (const [catKey, catValues] of Object.entries(category || {})) {
            const arr = []

            for (const [accKey, accVal] of access) {
                if (catValues[accKey]) {
                    arr.push([accKey, accVal])
                }
            }

            if (arr.length > 0) {
                result[catKey] = arr
            }
        }

        return result
    }

    if (blockedByFailedTier) {
        const blockedTierSettingId = blockedTierHistory?.tierSetting?._id || blockedTierHistory?.tierSetting;
        const failedTierSettingId = failedTierHistory?.tierSetting?._id || failedTierHistory?.tierSetting;
        const blockedTier = userVerificationTiers?.find(d => d._id === blockedTierSettingId) || (blockedTierHistory?.tierSetting?._id ? blockedTierHistory.tierSetting : null);
        const failedTier = userVerificationTiers?.find(d => d._id === failedTierSettingId) || (failedTierHistory?.tierSetting?._id ? failedTierHistory.tierSetting : null);
        const blockedTierName = extractLocalizedValue(blockedTier?.displayName) || blockedTier?.name;
        const failedTierName = extractLocalizedValue(failedTier?.displayName) || failedTier?.name;

        return (
            <Row gutter={[16, 16]}>
                {blockedTier && (
                    <Col span={24}>
                        <Card className="account-verification-card-selection h-full">
                            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                <Row justify="space-between" align="middle">
                                    <div className="account-verification-card-selection-title">{blockedTierName}</div>
                                    <Space className="ms-auto">
                                        {blockedTier?.tierLevel && (
                                            <Tag bordered={false} color="geekblue">{t("Tier")} {blockedTier.tierLevel}</Tag>
                                        )}
                                        <Tag color={getStatusColor(blockedTierHistory.status)} bordered={false}>
                                            {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                        </Tag>
                                    </Space>
                                </Row>
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t("accountVerification.blockedTier.alert", { failedLevel: failedTierHistory.tierLevel, failedTierName, blockedLevel: blockedTierHistory.tierLevel })}
                                />
                                <Row className="mt-2">
                                    <Col xs={24} md={12}>
                                        <Button
                                            danger
                                            loading={isCancelling}
                                            onClick={handleCancelBlockedTier}
                                        >
                                            {t("accountVerification.blockedTier.cancelButton", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </Button>
                                    </Col>
                                </Row>
                            </Space>
                        </Card>
                    </Col>
                )}
                {failedTier && (
                    <Col span={24}>
                        <Tooltip title={t("accountVerification.blockedTier.tooltip", { blockedLevel: blockedTierHistory.tierLevel })}>
                            <Card className="account-verification-card-selection h-full" style={{ opacity: 0.6 }}>
                                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                    <Row justify="space-between" align="middle">
                                        <div className="account-verification-card-selection-title">{failedTierName}</div>
                                        <Space className="ms-auto">
                                            {failedTier?.tierLevel && (
                                                <Tag bordered={false} color="geekblue">{t("Tier")} {failedTier.tierLevel}</Tag>
                                            )}
                                            <Tag color={getStatusColor(failedTierHistory.status)} bordered={false}>
                                                {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                            </Tag>
                                        </Space>
                                    </Row>
                                    <Space size={6}>
                                        <LockOutlined />
                                        <span className="secondary-text">
                                            {t("accountVerification.blockedTier.lockHint", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </span>
                                    </Space>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                )}
            </Row>
        );
    }

    return (
        <Row gutter={[16, 16]}>
            {(standaloneTier ? [standaloneTier] : userVerificationTiers)?.map((tier) => {
                const displayName = extractLocalizedValue(tier.displayName) || tier.name;
                const description = extractLocalizedValue(tier.description);
                const status = getTierStatus(tier._id);
                const tierVerificationHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
                const documents = tier?.documentSettings?.map(doc => extractLocalizedValue(doc?.documentSettingDoc?.displayName) || doc?.documentSettingDoc?.name)

                const accessRightsList = Object.entries(tier?.accessRights || {}) || []
                const access = accessRightsList.filter(item => item[1] === null)
                const accessWithAmounts = accessRightsList.filter(item => item[1] !== null)
                const nextButtonAvailable = (!currentTierAvailableId && (tier?.type === "standalone")) || tier?._id === currentTierAvailableId
                const accessWithCategory = mapCategoryAccess(accessRightsListCategory, access);

                return (
                    <Col className={"mh-100"} span={24} key={tier._id}>
                        <Tooltip placement="top">
                            <Card className="account-verification-card-selection h-full">
                                <Space direction="vertical" size={8} style={{ width: "100%", height: "100%" }}>
                                    {
                                        status ?
                                            <Tag color={getStatusColor(status)} bordered={false}>
                                                {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                            </Tag> : <></>
                                    }
                                    <div className="account-verification-card-selection-title">
                                        {displayName} {tier?.tierLevel ?
                                            <Tag className={"ms-1"}
                                                color={"geekblue"}>{t("Tier")} {tier?.tierLevel}</Tag> : ""}
                                    </div>

                                    <p>
                                        <small>{description}</small>
                                    </p>

                                    <Row gutter={4}>
                                        <Col span={12}>
                                            <p>Docs Required</p>
                                            <Space direction={"vertical"} wrap>
                                                {documents?.map(d => (
                                                    <div className={"documents-tag"} key={d}>{d}</div>
                                                ))}
                                            </Space>
                                        </Col>
                                        <Col span={12}>
                                            <p>Access</p>
                                            {
                                                accessWithAmounts?.filter(([_, {
                                                    dailyMax,
                                                    allTimeMax
                                                }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                    <>
                                                        {
                                                            accessWithAmounts?.map(
                                                                ([module, { dailyMax, allTimeMax }], i) => {
                                                                    return (
                                                                        (dailyMax > 0 || allTimeMax > 0) &&
                                                                        <div key={module}>
                                                                            <div
                                                                                className={"documents-tag"}>{t(`accountVerification.tierSettingAccessRight.${module}`)}</div>
                                                                            <div className={"documents-tag-value mb-1"}>
                                                                                {dailyMax > 0 ? `Daily Max ${dailyMax || "0"} USD` : ""}
                                                                                {dailyMax > 0 && allTimeMax > 0 && " | "}
                                                                                {allTimeMax > 0 ? `All Time Max ${allTimeMax || "0"} USD` : ""}
                                                                            </div>
                                                                        </div>

                                                                    )
                                                                }
                                                            )
                                                        }
                                                        <Divider className={"my-2"} />
                                                    </>
                                                )
                                            }
                                            <Collapse className={"access-rights-collapse"} ghost expandIcon={null}>
                                                {
                                                    Object.keys(accessWithCategory)?.map((cat) => (
                                                        <CollapsePanel
                                                            header={
                                                                <p>
                                                                    <small>{t(`accountVerification.tierSettingAccessRight.${cat?.replace(" ", "-")}`)}</small>
                                                                </p>
                                                            }
                                                            key={cat}
                                                        >
                                                            {
                                                                accessWithCategory[cat]?.map(
                                                                    ([module, value], i) => {
                                                                        return (
                                                                            <div className={"documents-tag"}
                                                                                key={module}>
                                                                                {t(`accountVerification.tierSettingAccessRight.${module}`)}
                                                                            </div>
                                                                        )
                                                                    }
                                                                )
                                                            }
                                                        </CollapsePanel>
                                                    ))
                                                }
                                            </Collapse>
                                        </Col>
                                    </Row>

                                    <Row justify={"end"} className={"mt-auto"}>
                                        {
                                            tierVerificationHistory?.status !== "verified" &&
                                            <Space>
                                                <Button
                                                    type={"default"}
                                                    className="statusButton mt-2 ms-auto"
                                                    onClick={() => handleSelectTier(tier)}
                                                    disabled={!nextButtonAvailable}
                                                >
                                                    {
                                                        (nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status)) ? t("Continue with last record")
                                                            : nextButtonAvailable ? t("common.next")
                                                                : tierVerificationHistory?.status === "pending_verification" ? t("successMsg.pendingApproval")
                                                                    : t("Unavailable")
                                                    }
                                                </Button>
                                                {
                                                    (nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status)) &&
                                                    <Button
                                                        type={"default"}
                                                        className="statusButton mt-2 ms-auto"
                                                        onClick={() => handleSelectTier(tier, true)}
                                                        disabled={!nextButtonAvailable}
                                                    >
                                                        Start New Record
                                                    </Button>
                                                }
                                            </Space>
                                        }
                                    </Row>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                );
            })}
        </Row>
    )
}

export default SelectTier;
